import { gql } from '@apollo/client'
import { Caption, Chip, Col, Container, Heading, Row } from '@farol-ds/react'
import { OfferLockCard } from '@jusbrasil-web/shared-apollo-client/types'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useCallback, useEffect, useState } from 'react'

import { PlanFrequency, PlanFrequencyLabel } from '../../../constants'
import { useRegisterLockEvent } from '../../../hooks'
import { LockEventName } from '../../../types'
import { FrequencyToggle } from '../../frequency-toggle'
import { PaywallCarousel } from '../../paywall-carousel'
import { JurisThreeOffersSwitchFrequencyLockPlanCard } from '../juris-three-offers-switch-frequency-lock-plan-card'
import {
  JurisThreeOffersPlanCard_OfferLockCardFragment,
  JurisThreeOffersPlanCard_ViewerFragment,
} from '../juris-three-offers-switch-frequency-lock-plan-card/__generated__/juris-three-offers-switch-frequency-lock-plan-card.graphql'
import { JurisThreeOffersSwitchFrequencyLockPlanCardPaywall } from '../juris-three-offers-switch-frequency-plan-card-paywall'
import { JurisThreeOffersPlanCardPaywall_ViewerFragment } from '../juris-three-offers-switch-frequency-plan-card-paywall/__generated__/juris-three-offers-switch-frequency-plan-card-paywall.graphql'
import {
  JurisThreeOffersSwitchFrequencyLockContent_OfferLockDataFragment,
  JurisThreeOffersSwitchFrequencyLockContent_ViewerFragment,
} from './__generated__/juris-three-offers-switch-frequency-lock-content.graphql'
import { ComparePlansAccordion } from './compare-plans-accordion'
import { RENEWAL_TEXTS } from './constants'
import styles from './juris-three-offers-switch-frequency-lock-content.module.scss'

export interface JurisThreeOffersSwitchFrequencyLockContentProps {
  offerLockData: JurisThreeOffersSwitchFrequencyLockContent_OfferLockDataFragment
  viewer: JurisThreeOffersSwitchFrequencyLockContent_ViewerFragment
  isPaywall?: boolean
}

type JurisThreeOffersCard =
  JurisThreeOffersSwitchFrequencyLockContent_OfferLockDataFragment['body']['cards'][number]

export function JurisThreeOffersSwitchFrequencyLockContent({
  offerLockData,
  viewer,
  isPaywall = false,
}: JurisThreeOffersSwitchFrequencyLockContentProps) {
  const { registerLockEvent } = useRegisterLockEvent()
  const { header, body } = offerLockData
  const { title, toggle } = header || {}
  const { options, defaultOption, description } = toggle || {}
  const { text, benefit } = description || {}

  const [monthlyFrequency] = options
  const { cards } = body || {}

  const isYearlyOfferWithCreditCard = (card: JurisThreeOffersCard) =>
    card.offer.frequency === PlanFrequency.YEARLY && !!card.creditCard

  const [currentFrequency, setCurrentFrequency] = useState<string>(defaultOption)
  const [currentCards, setCurrentCards] = useState(
    defaultOption === monthlyFrequency ? cards.slice(0, 3) : cards.slice(3)
  )
  const [isComparePlansOpen, setComparePlansOpen] = useState<boolean>(false)

  const { pid } = viewer || {}

  const getCardsForFrequency = useCallback(
    (frequency: string) => (frequency === monthlyFrequency ? cards.slice(0, 3) : cards.slice(3)),
    [monthlyFrequency, cards]
  )

  useEffect(() => {
    getCardsForFrequency(currentFrequency).forEach((card) =>
      registerLockEvent({
        eventName: LockEventName.LOCK_VIEW,
        eventProperties: [card.lockEventProperties],
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sendFrequencyToggleClickedEvent = useCallback((card?: JurisThreeOffersCard) => {
    if (!card) return
    const { lockEventProperties } = card
    if (!lockEventProperties) return

    const { experiment_id, feature, current_plan, next_plan, from_component, pid } =
      lockEventProperties
    sendMetrics('Box.FrequencyToggleClicked', pid ? String(pid) : null, {
      experiment_id,
      feature,
      current_plan,
      next_plan,
      from_component,
    })
  }, [])

  const handleComparePlansSectionClicked = useCallback(
    (value: string) => {
      const isOpen = !!value

      if (isOpen && !isComparePlansOpen)
        sendMetrics('Box.ComparePlansSectionClicked', pid ? String(pid) : null, {
          from_component: 'ComparePlansSection',
        })

      setComparePlansOpen(isOpen)
    },
    [isComparePlansOpen, pid]
  )

  const handleChangeFrequency = useCallback(
    (frequency: string) => {
      const newCards = getCardsForFrequency(frequency)
      sendFrequencyToggleClickedEvent(newCards[0])
      newCards.forEach((card) =>
        registerLockEvent({
          eventName: LockEventName.LOCK_VIEW,
          eventProperties: [card.lockEventProperties],
        })
      )
      setCurrentCards(newCards)
      setCurrentFrequency(frequency)
    },
    [getCardsForFrequency, registerLockEvent, sendFrequencyToggleClickedEvent]
  )

  const getRenewalText = () => {
    const isYearlyCurrentFrequency = currentFrequency === PlanFrequencyLabel.YEARLY
    const hasPromotionalPlans = currentCards.some(
      (card) => card.offer?.priceAfterPromotionalPeriodText
    )

    if (isYearlyCurrentFrequency && hasPromotionalPlans) return RENEWAL_TEXTS.YEARLY_PROMOTIONAL

    return isYearlyCurrentFrequency ? RENEWAL_TEXTS.YEARLY : RENEWAL_TEXTS.MONTHLY
  }

  const renderPlanCard = (card: JurisThreeOffersPlanCard_OfferLockCardFragment) => {
    const Component = isPaywall
      ? JurisThreeOffersSwitchFrequencyLockPlanCardPaywall
      : JurisThreeOffersSwitchFrequencyLockPlanCard

    return (
      <Component
        offerLockCard={card}
        viewer={
          isPaywall
            ? (viewer as JurisThreeOffersPlanCardPaywall_ViewerFragment)
            : (viewer as JurisThreeOffersPlanCard_ViewerFragment)
        }
        showCreditCardAndRemoveOneClick={isYearlyOfferWithCreditCard(card as OfferLockCard)}
      />
    )
  }

  return (
    <main>
      <Container>
        <Row className={styles.jurisThreeOffersSwitchFrequencyContentHeader} asChild>
          <section>
            <Col xs={12}>
              <Heading
                className={styles.jurisThreeOffersSwitchFrequencyContentHeaderHeading}
                size={isPaywall ? 'lg' : 'xl'}
                asChild
              >
                <h1>{title}</h1>
              </Heading>

              <FrequencyToggle
                options={options}
                currentFrequency={currentFrequency}
                onChangeFrequency={handleChangeFrequency}
              />

              <Caption className={styles.jurisThreeOffersSwitchFrequencyContentHeaderCaption}>
                {text}{' '}
                <Chip kind="secondary" size="sm">
                  <strong>{benefit}</strong>
                </Chip>
              </Caption>
            </Col>
          </section>
        </Row>

        {isPaywall ? (
          <PaywallCarousel<JurisThreeOffersPlanCard_OfferLockCardFragment>
            cards={currentCards}
            renderCard={renderPlanCard}
          />
        ) : (
          <>
            <Row>
              {currentCards.map((card, index) => {
                return (
                  <Col key={index} xs={12} md={4}>
                    {renderPlanCard(card)}
                  </Col>
                )
              })}
            </Row>

            <Row className={styles.jurisThreeOffersSwitchFrequencyContentRenewalText}>
              <Col xs={12}>
                <Caption>{getRenewalText()}</Caption>
              </Col>
            </Row>

            <Row className={styles.jurisThreeOffersSwitchFrequencyContentAccordion} asChild>
              <section>
                <Col xs={12}>
                  <ComparePlansAccordion onValueChange={handleComparePlansSectionClicked} />
                </Col>
              </section>
            </Row>
          </>
        )}
      </Container>
    </main>
  )
}

JurisThreeOffersSwitchFrequencyLockContent.fragments = {
  offerLockData: gql`
    fragment JurisThreeOffersSwitchFrequencyLockContent_offerLockData on OfferLockData {
      header {
        title
        toggle {
          options
          defaultOption
          description {
            text
            benefit
          }
        }
      }
      body {
        cards {
          ...JurisThreeOffersPlanCard_offerLockCard
          ...JurisThreeOffersPlanCardPaywall_offerLockCard
        }
      }
    }
    ${JurisThreeOffersSwitchFrequencyLockPlanCard.fragments.offerLockCard}
    ${JurisThreeOffersSwitchFrequencyLockPlanCardPaywall.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment JurisThreeOffersSwitchFrequencyLockContent_viewer on Profile {
      pid
      ...JurisThreeOffersPlanCardPaywall_viewer
      ...JurisThreeOffersPlanCard_viewer
    }
    ${JurisThreeOffersSwitchFrequencyLockPlanCardPaywall.fragments.viewer}
    ${JurisThreeOffersSwitchFrequencyLockPlanCard.fragments.viewer}
  `,
}
